import { FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, ApiError } from '../api';
import { useAuthStore } from '../auth';
import { AuthShell } from '../components/AuthShell';

export function SignupPage() {
  const navigate = useNavigate();
  const setTokens = useAuthStore((s) => s.setTokens);
  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    orgName: '',
  });
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function update<K extends keyof typeof form>(k: K, v: string) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const tokens = await api.signup(form);
      setTokens(tokens);
      navigate('/');
    } catch (e) {
      setErr(
        e instanceof ApiError
          ? humanizeError(e.message)
          : 'Network error',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthShell
      title="Create your organization"
      subtitle="ScoreLync orgs are reviewed by a platform admin before activation."
    >
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="label">Organization name</label>
          <input
            className="input"
            value={form.orgName}
            onChange={(e) => update('orgName', e.target.value)}
            placeholder="Acme Athletic Club"
            required
            minLength={2}
          />
        </div>
        <div>
          <label className="label">Your name</label>
          <input
            className="input"
            value={form.name}
            onChange={(e) => update('name', e.target.value)}
            required
            minLength={2}
          />
        </div>
        <div>
          <label className="label">Email</label>
          <input
            className="input"
            type="email"
            autoComplete="email"
            value={form.email}
            onChange={(e) => update('email', e.target.value)}
            required
          />
        </div>
        <div>
          <label className="label">Password</label>
          <input
            className="input"
            type="password"
            autoComplete="new-password"
            minLength={8}
            value={form.password}
            onChange={(e) => update('password', e.target.value)}
            required
          />
          <p className="mt-1 text-xs text-muted">Minimum 8 characters.</p>
        </div>
        {err && (
          <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
            {err}
          </div>
        )}
        <button className="btn btn-primary w-full" disabled={busy}>
          {busy ? 'Creating…' : 'Create organization →'}
        </button>
      </form>
      <p className="mt-6 text-center text-sm text-muted">
        Already have an account?{' '}
        <Link to="/login" className="text-brand-light hover:text-brand">
          Sign in
        </Link>
      </p>
    </AuthShell>
  );
}

function humanizeError(code: string): string {
  switch (code) {
    case 'email_already_in_use':
      return 'That email is already registered.';
    default:
      return code;
  }
}
